/**
 * Opt-in extras for `FleetOrderRow` (PLA-861, PSP view).
 *
 * Rendered only when the caller passes the matching optional prop; the B2B
 * FleetHub list never does, so its cards stay pixel-identical.
 *
 * @module components/FleetOrderRowExtras
 */

import { TextStyle, View, ViewStyle } from "react-native"

import { colors } from "@/theme"

import { Text } from "./Text"

/** One defect line under the result badge. */
export interface FleetOrderRowDefect {
  code: string
  text: string
  /** Defect class tag, e.g. "EM" / "GM" / "GefM". */
  cls: string
}

const MONO_FONT = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"

/** Index range of `query` inside `fin` (case-insensitive, spaces ignored). */
export function findFinHighlight(
  fin: string,
  query: string | undefined,
): { start: number; end: number } | null {
  const q = (query ?? "").toUpperCase().replace(/\s+/g, "")
  if (!q) return null
  const start = fin.toUpperCase().indexOf(q)
  return start < 0 ? null : { start, end: start + q.length }
}

/** "FIN WVWZZZ…" with the matched query part highlighted. */
export function FleetOrderRowFin({ fin, highlight }: { fin: string; highlight?: string }) {
  const range = findFinHighlight(fin, highlight)
  return (
    <Text style={$fin} accessibilityLabel={`FIN ${fin}`}>
      {"FIN "}
      {range ? (
        <>
          {fin.slice(0, range.start)}
          <Text style={[$fin, $finMark]}>{fin.slice(range.start, range.end)}</Text>
          {fin.slice(range.end)}
        </>
      ) : (
        fin
      )}
    </Text>
  )
}

/** Defect list with class tag, shown under a failed badge. */
export function FleetOrderRowDefects({ defects }: { defects: readonly FleetOrderRowDefect[] }) {
  return (
    <View style={$defectList} accessibilityRole="list">
      {defects.map((d, i) => (
        <View key={`${d.code}-${i}`} style={$defectItem}>
          <Text style={$defectClass}>{d.cls}</Text>
          <Text style={$defectText}>{d.text}</Text>
        </View>
      ))}
    </View>
  )
}

/** Chip shown in place of the plate for cars without one (used cars). */
export function FleetOrderRowNoPlate({ label }: { label: string }) {
  return (
    <View style={$noPlate}>
      <Text style={$noPlateText}>{label}</Text>
    </View>
  )
}

const $fin: TextStyle = {
  fontFamily: MONO_FONT,
  fontSize: 12,
  color: colors.text,
  letterSpacing: 0,
}

const $finMark: TextStyle = {
  backgroundColor: "rgba(255, 56, 47, 0.14)",
  borderRadius: 2,
}

const $defectList: ViewStyle = {
  gap: 3,
  marginTop: 2,
}

const $defectItem: ViewStyle = {
  flexDirection: "row",
  gap: 6,
}

const $defectClass: TextStyle = {
  fontSize: 12,
  fontWeight: "700",
  color: colors.text,
  minWidth: 30,
}

const $defectText: TextStyle = {
  fontSize: 12,
  color: colors.textDim,
  flexShrink: 1,
}

const $noPlate: ViewStyle = {
  alignSelf: "flex-start",
  backgroundColor: colors.surfaceSecondary,
  borderRadius: 6,
  paddingHorizontal: 8,
  paddingVertical: 4,
}

const $noPlateText: TextStyle = {
  fontSize: 11,
  fontWeight: "600",
  color: colors.textDim,
}
